/* eslint-disable no-underscore-dangle */
import {
  makeObservable, observable, runInAction, action,
} from 'mobx';
import ApiResponseError from './apiResponseError';
import captureException from './captureException';
import isAbortedError from './isAbortedError';

export type AbortableRequest = {
  response: Promise<Response>;
  readonly aborted: boolean;
  abort(): void;
};

export type AbortableRequester = (uri: string, init?: RequestInit) => AbortableRequest;

export type Requester = (uri: string, init?: RequestInit) => Promise<Response>;

export const withAbortable = (requester: Requester) => (
  uri: string,
  init?: RequestInit,
): AbortableRequest => {
  const controller = new AbortController();
  const { signal } = controller;
  return {
    response: requester(uri, { ...init, signal }),
    abort() {
      controller.abort();
    },
    get aborted() {
      return controller.signal.aborted;
    },
  };
};

/**
 * This creates a fetcher function that has builtin support for cancelling
 * the previous request when a new one is initiated.
 *
 * This works by adding an AbortController's signal to the fetch. When a
 * request is initiated, a reference is stored via a setter on a private field.
 * If the private field already has another reference for a request, the
 * previous request is aborted and the new request is stored on the private
 * field.
 *
 * Once a request completes, an additional check is made to see if the request
 * that has completed is still the most current request. This check is
 * performed by comparing the local reference to that on the private field used
 * to track the request. When the request is the most recent request, the
 * response is parsed and the onSuccess callback is called with the parsed
 * response data.
 *
 * @example
 * ```ts
 * const fetcher = new AbortableFetch(apiRequest, (data) => console.log(data));
 * fetcher.fetch(
 *   'https://example.com/api/data.json',
 *   {
 *     headers: {
 *       'Content-Type': 'application/json'
 *     }
 *   }
 * );
 * ```
 *
 */
export default class AbortableFetch<T> {
  constructor(
    requester: Requester,
    private onSuccess: (data: T) => void,
  ) {
    makeObservable(this, {
      isLoading: observable,
      fetch: action,
    });

    this.abortableRequester = withAbortable(requester);
  }

  private abortableRequester: AbortableRequester;

  private _inProcessFetch: AbortableRequest | null = null;

  public isLoading: boolean = false;

  private get inProcessFetch() {
    return this._inProcessFetch;
  }

  private set inProcessFetch(abortableRequest: AbortableRequest) {
    // Abort any previously requests that have not completed.
    if (this._inProcessFetch && !this._inProcessFetch.aborted) {
      this._inProcessFetch.abort();
    }

    this._inProcessFetch = abortableRequest;
  }

  async fetch(url: string, init?: RequestInit): Promise<void> {
    try {
      this.isLoading = true;
      const inProcessFetch = this.abortableRequester(url, init);
      this.inProcessFetch = inProcessFetch;
      const response = await this.inProcessFetch.response;

      if (!response.ok) {
        throw new ApiResponseError(response);
      }

      // Only parse the response if the fetch was not cancelled.
      if (this.inProcessFetch === inProcessFetch && !this.inProcessFetch.aborted) {
        const data = await response.json();
        // Only assign the value if the fetch closure reference is the current fetch.
        if (this.inProcessFetch === inProcessFetch && !this.inProcessFetch.aborted) {
          this.onSuccess(data);
        }
      }
    } catch (err) {
      if (err instanceof ApiResponseError) {
        captureException(
          err,
          {
            extra: {
              url: err.response.url,
            },
          },
        );
      } else if (!isAbortedError(err)) {
        captureException(err);
      }
    } finally {
      this.inProcessFetch = null;
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }
}
